import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useClipboard from "react-use-clipboard";

import { FreeKeeposLimitModal } from "../../components/FreeKeeposLimitModal";
import LaunchIcon from "../../components/Launch";
import { PlusOnlyModal } from "../../components/PlusOnlyModal";
import { PlusPromo } from "../../components/PlusPromo";
import { FreeTag, PlusTag } from "../../components/PlusTag";
import { Skeleton } from "../../components/Skeleton";
import ToggleSwitch from "../../components/ToggleSwitch";
import constants from "../../constants";
import { useAppState } from "../../contexts/App";
import { useSite } from "../../contexts/Site";
import { usePaymentsEligible } from "../../hooks/PaymentsEligible";
import { usePlusBenefits } from "../../hooks/PlusBenefits";
import { useToggleSiteActive } from "../../hooks/ToggleSiteActive";
import CalendarIcon from "../../icons/Calendar";
import ChartIcon from "../../icons/Chart";
import CheckIcon from "../../icons/Check";
import ChevronRightIcon from "../../icons/ChevronRight";
import EngineIcon from "../../icons/Engine";
import InboxIcon from "../../icons/Inbox";
import { hasActiveSubscription } from "../../services/api";
import { customDomainService } from "../../services/customDomain";
import {
  ActionCard,
  Actions,
  ActiveToggle,
  Container,
  Hero,
  HeroTop,
  LinkLine,
  Links,
  PlusBenefitsCard,
} from "./styles";

const EditorIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
  </svg>
);

const PagesIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M8 3h9l3 3v11a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    <path d="M4 7v12a2 2 0 0 0 2 2h10" />
  </svg>
);

const SearchIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

// The same as the menu's: a card with a person and the lines of their contact
const BusinessCardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <circle cx="8" cy="11" r="2" />
    <path d="M5 16a3 3 0 0 1 6 0M14 10h5M14 14h3" />
  </svg>
);

const CopyIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

// Last known custom domain of each Keepo (null: none connected), kept while the app is open
const knownCustomDomains = new Map<string, string | null>();

type LinkRowProps = {
  url: string;
  /** Shown right after the address (e.g. the PLUS tag of the custom domain). */
  tag?: React.ReactNode;
  canOpen: boolean;
  isActive: boolean;
};

/** One address of the Keepo with its copy/open actions (icon-only round buttons on phones). */
const LinkRow = ({ url, tag, canOpen, isActive }: LinkRowProps) => {
  const { t } = useTranslation();
  const [isCopied, setCopied] = useClipboard(url, { successDuration: 3000 });
  const copyLabel = isCopied ? t("pages.keepoDashboard.url.copied") : t("pages.keepoDashboard.url.copy");

  return (
    <LinkLine>
      <span className="address">
        <span className="url" title={url}>
          {url}
        </span>
        {tag}
      </span>
      <button
        type="button"
        className={isCopied ? "copied" : undefined}
        aria-label={copyLabel}
        title={copyLabel}
        onClick={setCopied}>
        {isCopied ? <CheckIcon color="currentColor" /> : <CopyIcon />}
        <span className="label">{copyLabel}</span>
      </button>
      {canOpen && (
        <button
          type="button"
          className={isActive ? undefined : "disabled"}
          aria-disabled={!isActive}
          aria-label={t("pages.keepoDashboard.url.open")}
          title={isActive ? t("pages.keepoDashboard.url.open") : t("pages.keepoDashboard.actions.open.disabled")}
          onClick={() => isActive && window.open(`//${url}`)}>
          <LaunchIcon color="currentColor" />
          <span className="label">{t("pages.keepoDashboard.url.open")}</span>
        </button>
      )}
    </LinkLine>
  );
};

// A banknote: the money the Keepo receives, in the same strokes as the other icons
const MoneyIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="2.5" />
    <path d="M6 10v4M18 10v4" />
  </svg>
);

const KeepoDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const paymentsEligible = usePaymentsEligible();
  const { site } = useSite();
  const { appState } = useAppState();

  const siteUrl = `${site.url}.${constants.nameSite}`;

  const isPlus = hasActiveSubscription(appState.editingSiteSubscription);
  const canOpen = !!site.url && appState.published;

  // A connected custom domain sits right after the Keepo address, which never moves: while it's being
  // asked for (PLUS only), that slot shows a placeholder. The last answer is remembered per Keepo, so
  // coming back to the dashboard shows it right away while it's refreshed.
  const siteId = appState.editingSiteId;
  const cachedDomain = siteId ? knownCustomDomains.get(siteId) : undefined;
  const [customDomain, setCustomDomain] = useState(cachedDomain || undefined);
  const [isDomainLoaded, setDomainLoaded] = useState(!isPlus || cachedDomain !== undefined);
  useEffect(() => {
    if (!isPlus || !siteId) {
      setDomainLoaded(true);
      return;
    }
    const cached = knownCustomDomains.get(siteId);
    setCustomDomain(cached || undefined);
    setDomainLoaded(cached !== undefined);

    let cancelled = false;
    customDomainService
      .get(siteId)
      .then(domain => {
        const connected = domain?.status === "connected" ? domain.domain : null;
        knownCustomDomains.set(siteId, connected);
        if (!cancelled) {
          setCustomDomain(connected || undefined);
        }
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setDomainLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [siteId, isPlus]);

  const plusBenefits = usePlusBenefits();
  const [showDomainPlusOnly, setShowDomainPlusOnly] = useState(false);

  const { isActive, isTogglingActive, toggleActive, showActivateLimitModal, closeActivateLimitModal } =
    useToggleSiteActive();

  return (
    <Container>
      <Hero>
        <HeroTop>
          <h2 className="title" title={site.title}>
            {site.title}
          </h2>
          <span className="tag">{isPlus ? <PlusTag /> : <FreeTag>{t("pages.keepoDashboard.plan.free")}</FreeTag>}</span>

          <ActiveToggle>
            <span>{isActive ? t("pages.keepoDashboard.active.on") : t("pages.keepoDashboard.active.off")}</span>
            <ToggleSwitch
              label={t("pages.settings.form.active")}
              value={isActive}
              setValue={toggleActive}
              disabled={isTogglingActive}
            />
          </ActiveToggle>
        </HeroTop>

        {site.url && (
          <Links>
            <LinkRow url={siteUrl} canOpen={canOpen} isActive={isActive} />
            {/* The custom domain's slot: placeholder while asking, the domain, or the offer to connect one
                (PLUS goes to the setup, FREE sees it's a PLUS feature) */}
            {!isDomainLoaded ? (
              <div className="skeleton" aria-hidden="true">
                <span />
              </div>
            ) : customDomain ? (
              <LinkRow url={customDomain} canOpen={canOpen} isActive={isActive} />
            ) : (
              <button
                type="button"
                className="connect-domain"
                onClick={() => (isPlus ? navigate("/settings/custom-domain") : setShowDomainPlusOnly(true))}>
                ＋ {t("pages.keepoDashboard.url.connectDomain")}
              </button>
            )}
          </Links>
        )}
      </Hero>

      {isPlus ? (
        <PlusBenefitsCard type="button" onClick={() => navigate("/keepo/plus")}>
          <PlusTag className="tag">✦ Keepo Plus</PlusTag>
          {/* Phones: the usage as the title, its bar right under it (as wide as the title). Desktop: the
              title, and the usage count with its bar on the right */}
          <span className="body">
            <h4 className="mobile-only">
              {t("pages.plusBenefits.usage", { used: plusBenefits.used, total: plusBenefits.total })}
            </h4>
            <h4 className="desktop-only">{t("pages.plusBenefits.title")}</h4>
            <span className="progress mobile-only" aria-hidden="true">
              <span style={{ width: `${(plusBenefits.used / plusBenefits.total) * 100}%` }} />
            </span>
          </span>
          <span className="usage desktop-only">
            <span className="count">
              {t("pages.plusBenefits.progress", { used: plusBenefits.used, total: plusBenefits.total })}
            </span>
            <span className="progress" aria-hidden="true">
              <span style={{ width: `${(plusBenefits.used / plusBenefits.total) * 100}%` }} />
            </span>
          </span>
          <span className="chevron">
            <ChevronRightIcon width={14} height={14} color="currentColor" />
          </span>
        </PlusBenefitsCard>
      ) : (
        <PlusPromo compact onClick={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)} />
      )}

      <Actions>
        <ActionCard type="button" onClick={() => navigate("/editor/pages")}>
          <span className="icon">
            <EditorIcon />
          </span>
          <h4>{t("pages.keepoDashboard.actions.editor.title")}</h4>
        </ActionCard>

        {/* The Keepo's pages: any plan builds them, tagged PLUS on a FREE Keepo (only PLUS publishes them) */}
        <ActionCard type="button" onClick={() => navigate("/editor/pages")}>
          <span className="icon">
            <PagesIcon />
          </span>
          <h4>{t("pages.keepoDashboard.actions.pages.title")}</h4>
          {!isPlus && <PlusTag className="plus-tag" />}
        </ActionCard>

        <ActionCard type="button" onClick={() => navigate("/analytics")}>
          <span className="icon">
            <ChartIcon color="currentColor" />
          </span>
          <h4>{t("pages.keepoDashboard.actions.analytics.title")}</h4>
          {/* PLUS, tagged as the others: opened all the same */}
          {!isPlus && <PlusTag className="plus-tag" />}
        </ActionCard>

        <ActionCard type="button" onClick={() => navigate("/contacts")}>
          <span className="icon">
            <InboxIcon color="currentColor" />
          </span>
          <h4>{t("pages.keepoDashboard.actions.contacts.title")}</h4>
        </ActionCard>

        {/* The agenda is a PLUS feature: tagged on a FREE Keepo, which still opens it (a lapsed PLUS keeps reading
            its bookings; the screen offers the plans) */}
        <ActionCard type="button" onClick={() => navigate("/agenda")}>
          <span className="icon">
            <CalendarIcon color="currentColor" />
          </span>
          <h4>{t("pages.keepoDashboard.actions.agenda.title")}</h4>
          {!isPlus && <PlusTag className="plus-tag" />}
        </ActionCard>

        {/* Cobranças: the money the Keepo receives online (any plan; who can charge is up to the registration) */}
        {/* only for people in Brazil (an account in reais). While the account loads its place is held (the screen doesn't
            jump when the card comes); when the account can't be read the card stays: the API has the last word */}
        {constants.payments && paymentsEligible === "loading" && (
          <Skeleton data-testid="charges-loading" aria-hidden className="h-[104px] w-full rounded-xl" />
        )}
        {constants.payments && (paymentsEligible === true || paymentsEligible === "error") && (
          <ActionCard type="button" onClick={() => navigate("/cobrancas")}>
            <span className="icon">
              <MoneyIcon />
            </span>
            <h4>{t("pages.keepoDashboard.actions.charges.title")}</h4>
            {/* what it charges for (the Agenda) is PLUS: tagged as the Agenda, and opened all the same */}
            {!isPlus && <PlusTag className="plus-tag" />}
          </ActionCard>
        )}

        <ActionCard type="button" onClick={() => navigate("/settings")}>
          <span className="icon">
            <EngineIcon color="currentColor" />
          </span>
          <h4>{t("pages.keepoDashboard.actions.settings.title")}</h4>
        </ActionCard>

        {/* SEO is a PLUS feature: FREE Keepos see it tagged and go to the plans */}
        <ActionCard
          type="button"
          onClick={() => navigate(isPlus ? "/seo" : `/subscription/features?site=${appState.editingSiteId}`)}>
          <span className="icon">
            <SearchIcon />
          </span>
          <h4>{t("pages.keepoDashboard.actions.seo.title")}</h4>
          {!isPlus && <PlusTag className="plus-tag" />}
        </ActionCard>

        {/* The Keepo's business card, already picked there (it starts at the Keepo being edited). Only once
            published: the card is its page as it is live (address, QR) */}
        {appState.published && (
          <ActionCard type="button" onClick={() => navigate("/business-card")}>
            <span className="icon">
              <BusinessCardIcon />
            </span>
            <h4>{t("components.appShell.nav.businessCard")}</h4>
          </ActionCard>
        )}
      </Actions>

      {showDomainPlusOnly && (
        <PlusOnlyModal
          feature={t("pages.settings.components.advancedConfigs.customDomain.title")}
          onClose={() => setShowDomainPlusOnly(false)}
        />
      )}
      {showActivateLimitModal && (
        <FreeKeeposLimitModal
          i18nKeyPrefix="pages.sites.actions.freeKeeposLimitReached"
          descriptionKey="reactivateDescription"
          onConfirm={() => navigate(`/subscription/features?site=${appState.editingSiteId}`)}
          onCancel={closeActivateLimitModal}
        />
      )}
    </Container>
  );
};

export default KeepoDashboardPage;
